import { TextFieldTypes } from '@ionic/core/components';
import { IonIcon, IonInput, IonItem, IonLabel, IonRow, IonSkeletonText, IonText, IonTextarea } from '@ionic/react';
import React from 'react';
import { Controller } from 'react-hook-form';
import { WrapperDiv } from './FormInput.styles';

interface IComponentProps {
  control: any;
  inputProps?: any;
  label?: string;
  name: string;
  placeholder: string;
  icon?: string;
  iconColor?: string;
  isTextArea?: boolean;
  onClick?: () => void;
  readonly?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  type?: TextFieldTypes;
  hasLine?: boolean;
  className?: string;
  position?: 'fixed' | 'stacked' | 'floating' | undefined;
  maxLength?: number;
  handleChange?: (value: string | number | null | undefined) => void;
  handleKeyDown?: (e: React.KeyboardEvent<HTMLIonInputElement>) => void;
}

interface IComponentPlusProps<TInput, TOutput> extends IComponentProps {
  transform: {
    input: (value: TOutput) => TInput;
    output: (value: CustomEvent) => TOutput;
  };
}

const ControllerPlus = <TInput extends string, TOutput>({
  control,
  transform,
  name,
  inputProps,
  label,
  placeholder,
  icon,
  iconColor,
  isTextArea,
  onClick,
  isLoading,
  disabled,
  type,
  hasLine,
  className,
  position,
  maxLength,
  handleChange,
}: IComponentPlusProps<TInput, TOutput>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error, isDirty } }) => {
        const onChangeHandler = (e: CustomEvent) => {
          onChange(transform.output(e));
          if (type === 'number') {
            handleChange && handleChange(+e.detail.value);
          } else {
            handleChange && handleChange(e.detail.value);
          }
        };

        return (
          <WrapperDiv className={className}>
            <IonRow className="form-row flexed col">
              {isLoading ? (
                <IonItem className="ion-no-padding flexed-fill">
                  <IonLabel position={position || 'stacked'}>{label}</IonLabel>
                  {isTextArea ? (
                    <>
                      <IonSkeletonText animated />
                      <IonSkeletonText animated />
                    </>
                  ) : (
                    <IonSkeletonText animated />
                  )}
                  {icon && <IonIcon icon={icon} size="small" color={iconColor || 'medium'} />}
                </IonItem>
              ) : (
                <div className="flexed-fill">
                  <IonItem className="ion-no-padding flexed-fill ion-no-margin" lines={hasLine === undefined ? 'full' : hasLine ? 'full' : 'none'}>
                    {isTextArea ? (
                      <IonTextarea
                        {...inputProps}
                        placeholder={placeholder}
                        onIonInput={(e) => onChangeHandler(e)}
                        value={value}
                        autoGrow
                        onClick={() => onClick && onClick()}
                        disabled={disabled}
                        maxlength={maxLength}
                        label={label}
                        labelPlacement={position || 'stacked'}
                      />
                    ) : (
                      <IonInput
                        {...inputProps}
                        placeholder={placeholder}
                        onIonInput={(e) => onChangeHandler(e)}
                        value={transform.input(value)}
                        onClick={() => onClick && onClick()}
                        disabled={disabled}
                        type={type || 'text'}
                        maxlength={maxLength}
                        label={label}
                        labelPlacement={position || 'stacked'}
                      />
                    )}
                    {icon && <IonIcon icon={icon} size="small" color={iconColor || 'medium'} />}
                  </IonItem>
                  {error && <IonText className="login-error">{error.message}</IonText>}
                </div>
              )}
            </IonRow>
          </WrapperDiv>
        );
      }}
    />
  );
};

const FormInput: React.FC<IComponentProps> = ({ control, inputProps, label, name, placeholder, icon, iconColor, isTextArea, onClick, readonly, isLoading, disabled, type, hasLine, className, position, maxLength, handleChange, handleKeyDown }) => {
  return type === 'number' ? (
    <ControllerPlus<string, number>
      transform={{
        input: (value) => (isNaN(value) || value === 0 ? '' : value.toString()),
        output: (e) => {
          const output = parseFloat(e.detail.value);
          return isNaN(output) ? 0 : output;
        },
      }}
      control={control}
      name={name}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      handleChange={handleChange}
      icon={icon}
      iconColor={iconColor}
      inputProps={inputProps}
      isLoading={isLoading}
      isTextArea={isTextArea}
      label={label}
      onClick={onClick}
      readonly={readonly}
      hasLine={hasLine}
      className={className}
      position={position}
      maxLength={maxLength}
      handleKeyDown={handleKeyDown}
    />
  ) : (
    <ControllerPlus<string, string>
      transform={{
        input: (value) => value,
        output: (e) => e.detail.value,
      }}
      control={control}
      name={name}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      handleChange={handleChange}
      icon={icon}
      iconColor={iconColor}
      inputProps={inputProps}
      isLoading={isLoading}
      isTextArea={isTextArea}
      label={label}
      onClick={onClick}
      readonly={readonly}
      hasLine={hasLine}
      className={className}
      position={position}
      maxLength={maxLength}
      handleKeyDown={handleKeyDown}
    />
  );
};

export default FormInput;
